import React from 'react'
import { LibraryConfigContext } from 'src/dynamic-config/LibraryConfigContext'
import { lit } from 'src/utils'

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    /**
     * The Button can be used as a link as well.
     * Within this prop one can pass the "a" tag (link).
     */
    as?: React.ElementType
    /**
     * `href` prop is only relevant (and should only be used) when `as='a'`
     */
    href?: string
    /**
     * The class name to be used for styling purposes
     */
    className?: string
    /**
     * The class name to be used to hook GTM event tracking
     */
    analyticsClassName?: string
    /**
     * Secondary style is used for outlined buttons.
     * */
    secondary?: boolean
    /**
     * If true, sets disabled style to button.
     * */
    disabled?: boolean
}

export const Button: React.FC<ButtonProps> = ({
    children,
    secondary,
    disabled,
    className,
    analyticsClassName,
    as: Tag = 'button',
    ...rest
}) => {
    const {
        theme: { mainColors },
    } = React.useContext(LibraryConfigContext)
    const primaryColor = mainColors.primary
    const primaryHoverColor = mainColors.primaryHover

    // constructing the color classes dynamically like this depends on our safelist configuration;
    // see tailwind.config.ts and tailwind-safelist.md for details.
    const primary = `border-transparent text-white bg-${primaryColor} hover:bg-${primaryHoverColor}`
    const reversed = `border-${primaryColor} text-${primaryColor} bg-white hover:border-${primaryHoverColor} hover:text-${primaryHoverColor}`
    const disabledVariant = `border-transparent text-neutral-400 bg-neutral-200`
    const variant = secondary ? reversed : primary
    const buttonStateClass = disabled ? disabledVariant : variant
    const dynamicProps = Tag === 'button' ? { type: 'button', ...rest } : rest
    return (
        <Tag
            {...dynamicProps}
            disabled={disabled}
            className={lit`
                rounded-sm border px-4 py-2 text-center text-base focus:outline-none focus:ring-2 focus:ring-offset-2
                ${analyticsClassName} ${className} ${buttonStateClass}
            `}
        >
            {children}
        </Tag>
    )
}
